<template>
  <section :class="$style.result" v-if="user != null && cluster != null">
    <p :class="[$style.title, $style.result__title]">お金の健康診断結果</p>
    <div :class="[$style.cluster, $style.result__cluster]">
      <p :class="[$style.cluster__name, $style.clusterName]">
        {{ clusterType }}
      </p>
      <p :class="[$style.cluster__average, $style.average]">
        同じグループの平均年齢:<span :class="$style['average--bold']"
          >{{
            numberToStringWithFirstDecimal(cluster.statistics.meanAge)
          }}歳</span
        >
        / 子どもの数:<span :class="$style['average--bold']"
          >{{
            numberToStringWithFirstDecimal(
              cluster.statistics.meanNumberOfChildren
            )
          }}人</span
        >
      </p>
    </div>
    <AnnualIncome
      :rank="data.annualIncome.rank"
      :user-value-string="formatMoney(data.annualIncome.userValue)"
      :text="clusterMeanValue(data.annualIncome.meanValue)"
    />
    <MonthlySavings
      :rank="data.monthlySavings.rank"
      :user-value-string="formatMoney(data.monthlySavings.userValue)"
      :text="clusterMeanValue(data.monthlySavings.meanValue)"
    />
    <TotalAssets
      :rank="data.totalAssets.rank"
      :user-value-string="formatMoney(data.totalAssets.userValue)"
      :text="clusterMeanValue(data.totalAssets.meanValue)"
    />
    <InvestmentRatio
      :investment="investment"
      :savings="savings"
      :text="`${clusterType}の平均 : ${numberToStringWithFirstDecimal(cluster.statistics.investmentRatio)}%`"
    />
    <DiagnosisType
      :class="[$style.diagnosisType, $style.result__diagnosisType]"
      :type="diagnosisSummary.type"
    />
    <MatchScore
      v-if="aiMatchScore != null"
      :class="[$style.matchScore, $style.result__matchScore]"
      :ai-match-score="aiMatchScore"
    />
    <SimpleLifePlanning
      v-if="
        diagnosisUuid != null &&
        cluster.householdStructure !== HouseholdStructureType.Retired
      "
      :class="[$style.lifePlanning, $style.result__lifePlanning]"
      :diagnosis-uuid="diagnosisUuid"
    />
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AnnualIncome from './AnnualIncome.vue'
import MonthlySavings from './MonthlySavings.vue'
import TotalAssets from './TotalAssets.vue'
import InvestmentRatio from './InvestmentRatio.vue'
import DiagnosisType from './DiagnosisType.vue'
import MatchScore from './MatchScore.vue'
import SimpleLifePlanning from './SimpleLifePlanning/SimpleLifePlanning.vue'
import { toYenString, toYenStringWithFirstDecimal } from './yenScale'
import { householdStructureTypeLabel } from './householdStructureType'
import { HouseholdStructureType } from '@/types/schema.generated'
import type {
  UserDiagnosisResultV2KarteFragment,
  UserDiagnosisResultV2DiagnosisSummaryFragment,
} from './UserDiagnosisResultV2.generated'

const props = defineProps<{
  result: UserDiagnosisResultV2KarteFragment
  summary: UserDiagnosisResultV2DiagnosisSummaryFragment
  karteId: string
}>()

const user = computed(() => props.result.user)

const diagnosisUuid = computed(() => props.result.diagnosis.uuid)

const aiMatchScore = computed(() => props.result.diagnosis.aiMatchScore)

const diagnosisSummary = computed(() => props.summary)

const cluster = computed(() => diagnosisSummary.value?.cluster)

const area = computed(() =>
  cluster.value?.residentialArea.prefecture == null
    ? cluster.value?.residentialArea.country
    : `${cluster.value?.residentialArea.prefecture} ${cluster.value?.residentialArea.city ?? ''} ${cluster.value?.residentialArea.town ?? ''}`
)

const investment = computed(() => props.result.diagnosis.investment ?? 0)
const savings = computed(() => props.result.diagnosis.saving ?? 0)
const annualIncome = computed(() => props.result.diagnosis.annualIncome ?? 0)
const partnerAnnualIncome = computed(
  () => props.result.diagnosis.partner?.annualIncome ?? 0
)

const data = computed(() => ({
  annualIncome: {
    rank: diagnosisSummary.value?.householdAnnualIncomeRank ?? '',
    userValue: annualIncome.value + partnerAnnualIncome.value,
    meanValue: cluster.value?.statistics.meanHouseholdAnnualIncome ?? 0,
  },
  monthlySavings: {
    rank: diagnosisSummary.value?.monthlySavingRank ?? '',
    userValue: props.result.diagnosis.savingMonth ?? 0,
    meanValue: cluster.value?.statistics.meanMonthlySaving ?? 0,
  },
  totalAssets: {
    rank: diagnosisSummary.value?.assetsRank ?? '',
    userValue: investment.value + savings.value,
    meanValue: cluster.value?.statistics.meanAssets ?? 0,
  },
}))

const formatMoney = (value: number) => {
  if (Math.abs(value) >= 100_000_000) {
    return toYenStringWithFirstDecimal(value)
  }

  return toYenString(value)
}

const householdStructureLabel = computed(() =>
  householdStructureTypeLabel(
    cluster.value?.householdStructure ?? HouseholdStructureType.Unspecified
  )
)

const clusterType = computed(
  () => `${area.value} x ${householdStructureLabel.value}`
)

const clusterMeanValue = (value: number) =>
  `${clusterType.value}の平均 : ${toYenString(value)}`

const numberToStringWithFirstDecimal = (value: number) =>
  value.toLocaleString(undefined, {
    maximumFractionDigits: 1,
  })
</script>

<style lang="scss" module>
.result {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px 8px;

  &__title {
    grid-column: 1 / 5;
  }

  &__cluster {
    grid-column: 1 / 5;
  }

  &__diagnosisType {
    grid-column: 1 / 3;
  }

  &__matchScore {
    grid-column: 1 / 3;
    grid-row: 5 / 6;
  }

  &__lifePlanning {
    grid-column: 3 / 5;
    grid-row: 4 / 6;
  }
}

.title {
  font-size: 24px;
  font-weight: bold;
  text-align: center;
}

.cluster {
  background-color: $color-surface-accent-primary;
  color: $color-text-and-object-high-emphasis-inverse;
  padding: 8px;
  border-radius: $border-radius-v2-base;
}

.clusterName {
  font-size: 20px;
  font-weight: bold;
  text-align: center;
}

.average {
  font-size: 12px;
  text-align: center;

  &--bold {
    font-weight: bold;
  }

  &:nth-child(n + 2) {
    margin-top: 4px;
  }
}
</style>
